(() => {
  'use strict';

  const root = document.querySelector('[data-ops-living-atmosphere]');
  if (!root || root.dataset.opsAtmosphereReady === 'true') return;
  root.dataset.opsAtmosphereReady = 'true';

  const scriptLayer = root.querySelector('[data-ops-living-script]');
  const artLayer = root.querySelector('[data-ops-living-art]');
  const whisper = root.querySelector('[data-ops-living-whisper]');
  const context = String(root.dataset.context || 'page').toLowerCase();
  const allowPersonalArt = root.dataset.personalArt === 'true';
  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isSmall = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  const objectUrls = [];

  const shuffle = (items) => items
    .map((value) => ({ value, order: Math.random() }))
    .sort((a, b) => a.order - b.order)
    .map((item) => item.value);

  const basePhrases = [
    'Abracadabra begins as writing before it becomes an image: a word reduced line by line until the page itself seems to swallow it.',
    'A pentagram is simple enough to draw in a single line and complicated enough to gather centuries of competing meanings.',
    'Old divination manuals turn weather, wood, rings, cards, dreams and chance meetings into possible languages of signs.',
    'A magic square is mathematics first; occult traditions later gave numbered grids planets, metals, names and traced lines.',
    'The Fibonacci sequence grows by remembering its previous two numbers: 1, 1, 2, 3, 5, 8, 13, 21.',
    'A seal can be a mark of identity, authority, protection, ritual structure or simply a symbol whose history has outgrown its maker.',
    'Some archive entries begin with a famous image. Others begin with one strange sentence buried in an old index.',
    'The deeper shelves are full of tiny methods: grain in a circle, a ring above letters, dry wood in a path, clouds treated as omens.',
    'An amulet is interesting even before belief enters the room: somebody chose an object, carried it, hid it, wore it or gave it a purpose.',
    'A manuscript margin can hold a correction, a warning, a diagram, a private joke or the beginning of an entirely different rabbit hole.',
    'Ink remembers sequence. One mark lies beneath the next even when the finished surface refuses to show how it was built.',
    'The archive does not get stranger in a straight line. Familiar symbols often have the deepest roots and obscure words sometimes lead back to ordinary things.'
  ];

  const collectionPhrases = [
    'Cut lines, backing paper, crop marks and tiny scraps are part of the visual language of making, not just the machinery behind it.',
    'A finished print has a visible face and an invisible history of layers, measurements, tests and decisions underneath it.',
    'The same symbol can become a card, sticker, print, label or banner; scale changes the way the mark behaves.',
    'Paper has edges. Ink has weight. A design that works at ten millimetres may become a completely different creature at a metre.'
  ];

  const makerPhrases = [
    'Measure first. Proof the edge. Keep the original. The ritual is less mysterious when the file survives it.',
    'A hidden layer can remain part of the project even when the final visible print completely covers it.',
    'The working file remembers what the finished object does not show.',
    'Crop marks, margins and bleed are quiet geometry: the invisible structure that lets the visible image land where it should.'
  ];

  const homePhrases = [
    'The studio is part catalogue, part workbench and part archive. A finished object can still lead back to the idea that made it.',
    'Somewhere between artwork and object there is always a stage where the image becomes material.',
    'The useful rabbit hole is the one that eventually returns with something worth making.'
  ];

  const whisperPhrases = [
    'The margin changed while you were looking elsewhere.',
    'Some marks feel familiar before their meaning is remembered.',
    'Not every layer needs to be visible to remain part of the object.',
    'The archive has more than one way down.',
    'A symbol can survive its explanation.',
    'The next line was always going to begin here.'
  ];

  const pagePool = () => {
    const archivePhrases = Array.isArray(window.OPS_OCCULT_ATMOSPHERE_PHRASES) ? window.OPS_OCCULT_ATMOSPHERE_PHRASES : [];
    const pool = [...archivePhrases, ...basePhrases];
    if (context.startsWith('collection-')) pool.push(...collectionPhrases);
    if (context.includes('builder') || context.includes('artwork') || context.includes('my-ops')) pool.push(...makerPhrases);
    if (context === 'home') pool.push(...homePhrases, ...collectionPhrases);
    return pool;
  };

  const startWriting = () => {
    if (!scriptLayer) return;
    let pool = shuffle(pagePool());
    let poolIndex = 0;
    const nextPhrase = () => {
      if (poolIndex >= pool.length) { pool = shuffle(pagePool()); poolIndex = 0; }
      return pool[poolIndex++];
    };
    const lineCount = isSmall ? 3 : 5;
    const lines = Array.from({ length: lineCount }, (_, index) => {
      const line = document.createElement('p');
      line.className = 'ops-living-atmosphere__line';
      line.style.setProperty('--ops-live-line', index);
      scriptLayer.appendChild(line);
      return line;
    });

    const write = (line, text, delay = 0) => {
      if (reducedMotion) {
        line.textContent = text;
        line.classList.add('is-set');
        return;
      }
      line.textContent = '';
      line.classList.remove('is-fading', 'is-set');
      line.classList.add('is-writing');
      let index = 0;
      const tick = () => {
        if (index < text.length) {
          const step = text.charAt(index) === ' ' ? Math.min(2, text.length - index) : 1;
          index = Math.min(text.length, index + step);
          line.textContent = text.slice(0, index);
          window.setTimeout(tick, 52 + Math.floor(Math.random() * 64));
          return;
        }
        // Completed manuscript lines stay fully readable. Animation happens to the line,
        // not to individual letters, so words can never appear damaged or incomplete.
        line.textContent = text;
        line.classList.remove('is-writing');
        line.classList.add('is-set');
        window.setTimeout(() => {
          line.classList.add('is-fading');
          window.setTimeout(() => write(line, nextPhrase(), 0), 2600);
        }, 26000 + Math.floor(Math.random() * 17000));
      };
      window.setTimeout(tick, delay);
    };

    lines.forEach((line, index) => write(line, nextPhrase(), index * 4200));
  };

  const openArtworkDb = () => new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) { reject(new Error('IndexedDB unavailable')); return; }
    const request = indexedDB.open('ops-artwork-scratchpad', 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains('uploads')) request.result.createObjectStore('uploads', { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('My Artwork unavailable'));
  });

  const getSavedArtwork = async () => {
    const db = await openArtworkDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('uploads', 'readonly');
      const request = tx.objectStore('uploads').getAll();
      tx.oncomplete = () => { db.close(); resolve(request.result || []); };
      tx.onerror = () => { db.close(); reject(tx.error || request.error || new Error('My Artwork unavailable')); };
    });
  };

  const makeArtTile = (record, index, total) => {
    const source = record.thumbnail instanceof Blob ? record.thumbnail : (record.file instanceof Blob ? record.file : null);
    if (!source) return null;
    const url = URL.createObjectURL(source);
    objectUrls.push(url);
    const image = document.createElement('img');
    image.className = 'ops-living-atmosphere__saved-art';
    image.src = url;
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    const side = index % 2 === 0 ? 'left' : 'right';
    const lane = Math.floor(index / 2);
    const y = 4 + ((lane * (82 / Math.max(1, Math.ceil(total / 2)))) + Math.random() * 8) % 86;
    const width = (isSmall ? 150 : 230) + Math.round(Math.random() * (isSmall ? 90 : 270));
    const x = side === 'left' ? -8 + Math.random() * 14 : 76 + Math.random() * 20;
    image.style.setProperty('--ops-art-x', `${x}vw`);
    image.style.setProperty('--ops-art-y', `${y}vh`);
    image.style.setProperty('--ops-art-w', `${width}px`);
    image.style.setProperty('--ops-art-r', `${Math.round((Math.random() - .5) * 15)}deg`);
    image.style.setProperty('--ops-art-delay', `${-Math.round(Math.random() * 24)}s`);
    image.style.setProperty('--ops-art-duration', `${42 + Math.round(Math.random() * 38)}s`);
    return image;
  };

  const startPersonalArtwork = async () => {
    if (!allowPersonalArt || !artLayer) return [];
    try {
      const rows = (await getSavedArtwork())
        .filter((record) => !record.temporaryProofBridge && !record.temporaryBuilderBridge && !record.temporaryBridge)
        .filter((record) => record.thumbnail instanceof Blob || record.file instanceof Blob)
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
      const limit = isSmall ? 6 : 20;
      const chosen = rows.slice(0, limit);
      chosen.forEach((record, index) => {
        const tile = makeArtTile(record, index, chosen.length);
        if (tile) artLayer.appendChild(tile);
      });
      if (chosen.length) root.classList.add('has-personal-art');
      return chosen;
    } catch {
      return [];
    }
  };

  const startWhispers = (personalRows = []) => {
    if (!whisper || reducedMotion) return;
    const personalNames = personalRows
      .map((record) => String(record.name || '').trim())
      .filter(Boolean)
      .slice(0, 6);
    const makePool = () => {
      const phrases = [...whisperPhrases];
      personalNames.forEach((name) => phrases.push(`A saved mark remains in this device's archive: ${name}.`));
      return phrases;
    };
    const reveal = () => {
      const pool = makePool();
      whisper.textContent = pool[Math.floor(Math.random() * pool.length)];
      whisper.style.setProperty('--ops-whisper-y', `${14 + Math.round(Math.random() * 66)}vh`);
      whisper.classList.add('is-visible');
      window.setTimeout(() => whisper.classList.remove('is-visible'), 5200 + Math.round(Math.random() * 2800));
      window.setTimeout(reveal, 52000 + Math.round(Math.random() * 52000));
    };
    window.setTimeout(reveal, 26000 + Math.round(Math.random() * 28000));
  };

  startWriting();
  startPersonalArtwork().then(startWhispers);

  window.addEventListener('pagehide', () => {
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }, { once: true });
})();
